"use client";

import { Button } from "@ui/components/core/Button";
import { ProgressBar } from "./ProgressIndicator";
import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import type { UseContactSalesFormV2Return } from "../hooks/useContactSalesFormV2";
import type { ContactSalesFormValues } from "../shared/types";

type FormStateType = UseContactSalesFormV2Return["form"]["state"];

type ButtonConfig = {
  type: "next" | "back" | "submit";
  label: string;
  variant: "primary" | "secondary" | "outline";
  validateFields?: (keyof ContactSalesFormValues)[];
  onClick?: () => void | Promise<void>;
};

type NavigationConfig = {
  showProgressBar: boolean;
  buttons: ButtonConfig[];
} | null;

interface StepNavigationProps {
  navigation: NavigationConfig;
  currentStep: number;
  totalSteps: number;
  form: UseContactSalesFormV2Return["form"];
}

interface NavigationButtonProps {
  buttonConfig: ButtonConfig;
  form: UseContactSalesFormV2Return["form"];
}

function NavigationButton({ buttonConfig, form }: NavigationButtonProps) {
  const { type, label, variant, validateFields, onClick } = buttonConfig;

  if (type === "back") {
    return (
      <Button type='button' onClick={onClick} variant={variant} size='md'>
        <ChevronLeft16 />
      </Button>
    );
  }

  if (type === "next" && validateFields) {
    return (
      <form.Subscribe
        selector={(state: FormStateType) => {
          const allValid = validateFields.every(fieldName => {
            const fieldMeta = state.fieldMeta[fieldName];
            return fieldMeta?.isValid === true;
          });
          return allValid;
        }}
      >
        {(canMoveNext: boolean) => (
          <Button
            type='button'
            onClick={() => onClick && void onClick()}
            disabled={!canMoveNext}
            variant={variant}
            size='md'
          >
            {label}
          </Button>
        )}
      </form.Subscribe>
    );
  }

  if (type === "submit") {
    return (
      <form.Subscribe
        selector={(state: FormStateType) => ({
          canSubmit: state.canSubmit,
          isSubmitting: state.isSubmitting,
        })}
      >
        {({ canSubmit, isSubmitting }: { canSubmit: boolean; isSubmitting: boolean }) => (
          <Button type='submit' disabled={!canSubmit || isSubmitting} variant={variant} size='md'>
            {label}
          </Button>
        )}
      </form.Subscribe>
    );
  }

  return null;
}

export function StepNavigation({ navigation, currentStep, totalSteps, form }: StepNavigationProps) {
  if (!navigation) {
    return null;
  }

  return (
    <div className='tw-flex tw-items-center tw-justify-end md:tw-justify-between tw-mt-8 tw-pt-6'>
      <div className='tw-flex tw-gap-1'>
        {navigation.buttons.map((button, index) => (
          <NavigationButton key={index} buttonConfig={button} form={form} />
        ))}
      </div>
      {navigation.showProgressBar && (
        <div className='tw-flex-1 tw-max-w-60 tw-hidden md:tw-block'>
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </div>
      )}
    </div>
  );
}
